import { Component, NgZone, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { Observable, Subscription } from 'rxjs';
import { CollectionsService } from '../../../collections/service/collections.service';
import { ModalService } from '../../../shared/services/modal.service';
import { CartItem } from '../../interfaces/cart.interface';
import { CartService } from '../../services/cart.service';
import { EmptyCart } from '../../store/actions/cart.actions';
import i18next from 'i18next';
import { formatCurrency, getCurrencySymbol } from '@angular/common';
import {
  DataService,
  extractErrorMessage,
} from '../../../../core/services/data.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NativeIntegrationService } from '../../../../core/services/native-integration.service';
import { BridgeMode } from '@hg-com/xwc-js-bridge/dist/native-app.service';
import { B2BService } from 'src/core/services/b2b.service';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from 'src/core/services/auth.service';
import { AnalyticsService } from 'src/core/services/analytics.service';
import { environment } from 'src/environments/environment';
import { I18NextService } from 'angular-i18next';
import { formatNumber } from 'src/util';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent implements OnInit {
  cartOpen = false;
  eanOpen = false;
  cartItems: CartItem[] | undefined = [];
  cart: Observable<CartItem[]> | undefined;
  cartTotal = 0;
  priceActive = false;
  lang!: string;
  nativeOfflineReady = true;
  currencySymbol!: string;
  vendorName!: string;
  pdfAddress!: any[];
  pdfForm = this.fb.group({
    vendorName: [''],
    telefon: [''],
    address1: [''],
    address2: [''],
    address3: [''],
    address4: [''],
  });
  sflogniform = this.fb.group({
    username: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required]],
  });
  missingSkus: number[] = [];
  transferedSkus: number[] = [];
  private subscriptions: Subscription = new Subscription();

  constructor(
    public cartService: CartService,
    public collectionsService: CollectionsService,
    public dataService: DataService,
    private modalService: ModalService,
    private nis: NativeIntegrationService,
    private fb: FormBuilder,
    private router: Router,
    private store: Store<{ cart: CartItem[] }>,
    private b2bService: B2BService,
    private toastr: ToastrService,
    private authService: AuthService,
    private analyticsService: AnalyticsService,
    private ngZone: NgZone,
    private i18next: I18NextService
  ) {}

  ngOnInit(): void {
    if (this.nis.native.mode > BridgeMode.Mock) {
      const offlineModeSub = this.nis.offlineMode$.subscribe((offlineMode) => {
        this.nativeOfflineReady = offlineMode;
        console.log(offlineMode);
      });
      this.subscriptions.add(offlineModeSub);
    }
    this.currencySymbol = getCurrencySymbol(
      this.collectionsService.currencyCode,
      'narrow'
    );
    this.lang = i18next.language;
    const cartOpenSub = this.cartService.cartOpen$.subscribe((value) => {
      this.cartOpen = value;
    });
    this.subscriptions.add(cartOpenSub);

    const eanOpenSub = this.cartService.eanOpen$.subscribe((value) => {
      this.eanOpen = value;
    });
    this.subscriptions.add(eanOpenSub);
    const priceActiveSub = this.cartService.priceActive$.subscribe((value) => {
      this.priceActive = value;
    });
    this.subscriptions.add(priceActiveSub);
    this.cart = this.store.select('cart');
    const cartSub = this.cart.subscribe((cartItems) => {
      this.cartItems = cartItems;
      this.cartTotal = this.cartItems.reduce(
        (subtotal, item) => subtotal + item.price * item.quantity,
        0
      );
    });

    this.subscriptions.add(cartSub);

    const b2bTokensub = this.authService.communityToken.subscribe((x) => {
      this.ngZone.run(() => (this.communityData = x));
    });

    this.subscriptions.add(b2bTokensub);
  }

  emptyCart(): void {
    this.store.dispatch(EmptyCart());
    this.closeModal('empty-cart-modal');
    // this.router.navigate(['/home']);
    this.cartService.cartOpen$.next(false);
  }

  handleCart(): void {
    this.cartService.cartOpen$.next(!this.cartOpen);
    this.cartService.eanOpen$.next(false);
  }

  handleEan(): void {
    this.cartService.eanOpen$.next(!this.eanOpen);
    if (this.eanOpen) {
      this.analyticsService.trackEvent({
        event: 'virtualpageview',
        page: '/applic/bpl-app/virtual/show-gtin-codes',
        category: 'bpl app',
      });
    }
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 10);
  }

  openModal(id: string): void {
    this.modalService.open(id);
  }

  async handleGeneratePdfClick(): Promise<void> {
    this.openModal('generate-pdf');
  }

  closeModal(id: string): void {
    this.modalService.close(id);
  }

  generateCSV(form: FormGroup): void {
    const pdfAddress = [
      form.value.address1 ? form.value.address1 : ' ',
      form.value.address2 ? form.value.address2 : ' ',
      form.value.address3 ? form.value.address3 + ' ' : ' ',
      form.value.address4 ? form.value.address4 : ' ',
      form.value.telefon ? form.value.telefon : ' ',
    ];
    const prices = this.cartItems?.map(
      (x) => (x.product.price ?? 0) * x.quantity
    ) ?? [0];
    const sum = prices.reduce((acc, cur) => acc + cur, 0);
    const sumText = formatCurrency(sum, this.lang, this.currencySymbol) ?? '';
    const translations = [
      i18next.t('cart.pdf-article'),
      i18next.t('cart.pdf-dimensions'),
      i18next.t('cart.pdf-article-number'),
      this.priceActive ? i18next.t('cart.pdf-price') : '',
      i18next.t('cart.pdf-amount'),
      i18next.t('cart.sum'),
      sumText,
    ];

    const itemsPDF: {
      product: {
        texts: {
          name: string | undefined;
          dimensions: string | undefined;
          volume: string | undefined;
          title: string;
        };
        images: {
          freestanding_1: string;
          freestanding_2?: string | undefined;
          freestanding_3?: string | undefined;
          freestanding_mood_1?: string | undefined;
          freestanding_mood_2?: string | undefined;
          freestanding_mood_3?: string | undefined;
          milieu_1?: string | undefined;
          usp_design_1?: string | undefined;
          usp_function_1?: string | undefined;
          usp_function_2?: string | undefined;
          usp_function_3?: string | undefined;
          in_use_1?: string | undefined;
          usp_design_2?: string | undefined;
          in_use_2?: string | undefined;
          milieu_2?: string | undefined;
          collection?: string | undefined;
          packaging?: string | undefined;
          logo?: string | undefined;
          gift?: string | undefined;
        };
        collection: string;
        dishwasher_safe: boolean | undefined;
        gtin: number;
        microwave_safe: boolean | undefined;
        packaging_unit: number | undefined;
        publication_year: number | undefined;
        sku: number;
        assortment: string | undefined;
        price: number | undefined;
      };
      price_text: string;
      collection: { texts: { title: string } };
      quantity: number;
      price: number;
      collectionId: string;
      productId: string;
    }[] = [];
    this.cartItems?.forEach((cartItem) => {
      console.log(
        formatCurrency(
          cartItem.product.price ? cartItem.product.price : 0,
          this.lang,
          this.currencySymbol
        )
      );
      const generatedItem = {
        ...cartItem,
        product: {
          texts: {
            name: cartItem.product.texts.name
              ? i18next.t(cartItem.product.texts.name)
              : undefined,
            dimensions: cartItem.product.texts.dimensions
              ? i18next.t(cartItem.product.texts.dimensions)
              : undefined,
            volume: cartItem.product.texts.volume_text
              ? i18next.t(cartItem.product.texts.volume_text)
              : undefined,
            title: i18next.t(cartItem.product.texts.title),
          },
          images: {
            ...cartItem.product.images,
          },
          collection: cartItem.product.collection,
          dishwasher_safe: cartItem.product.dishwasher_safe,
          gtin: cartItem.product.gtin,
          microwave_safe: cartItem.product.microwave_safe,
          packaging_unit: cartItem.product.packaging_unit,
          publication_year: cartItem.product.publication_year,
          sku: cartItem.product.sku,
          assortment: cartItem.product.assortment,
          price: this.priceActive ? cartItem.product.price : 0,
        },
        price_text: this.priceActive
          ? formatCurrency(
              cartItem.product.price ? cartItem.product.price : 0,
              this.lang,
              this.currencySymbol
            )
          : '',
        collection: {
          texts: {
            title: i18next.t(
              this.collectionsService.getCollectionData(cartItem.collectionId)
                .texts.title
            ),
          },
        },
      };
      itemsPDF.push(generatedItem);
    });

    this.dataService.generateXslx(
      itemsPDF,
      form.value.vendorName,
      this.lang,
      pdfAddress,
      translations
    );

    this.closeModal('generate-csv');
  }

  generatePDF(form: FormGroup): void {
    const pdfAddress = [
      form.value.address1 ? form.value.address1 : ' ',
      form.value.address2 ? form.value.address2 : ' ',
      form.value.address3 ? form.value.address3 + ' ' : ' ',
      form.value.address4 ? form.value.address4 : ' ',
      form.value.telefon ? form.value.telefon : ' ',
    ];
    const prices = this.cartItems?.map(
      (x) => (x.product.price ?? 0) * x.quantity
    ) ?? [0];
    const sum = prices.reduce((acc, cur) => acc + cur, 0);
    const sumText = formatCurrency(sum, this.lang, this.currencySymbol) ?? '';
    const translations = [
      i18next.t('cart.pdf-article'),
      i18next.t('cart.pdf-dimensions'),
      i18next.t('cart.pdf-article-number'),
      this.priceActive ? i18next.t('cart.pdf-price') : '',
      i18next.t('cart.pdf-amount'),
      i18next.t('cart.sum'),
      sumText,
    ];

    const itemsPDF: any[] = [];
    this.cartItems?.forEach((cartItem) => {
      //console.log(formatCurrency(cartItem.product.price ? cartItem.product.price : 0, this.lang, this.currencySymbol));
      const generatedItem = {
        ...cartItem,
        product: {
          texts: {
            name: cartItem.product.texts.name
              ? i18next.t(cartItem.product.texts.name)
              : undefined,
            dimensions: cartItem.product.texts.dimensions
              ? i18next.t(cartItem.product.texts.dimensions)
              : undefined,
            volume: cartItem.product.texts.volume_text
              ? i18next.t(cartItem.product.texts.volume_text)
              : undefined,
            title: i18next.t(cartItem.product.texts.title),
          },
          images: {
            ...cartItem.product.images,
          },
          collection: cartItem.product.collection,
          dishwasher_safe: cartItem.product.dishwasher_safe,
          gtin: cartItem.product.gtin,
          microwave_safe: cartItem.product.microwave_safe,
          packaging_unit: cartItem.product.packaging_unit,
          publication_year: cartItem.product.publication_year,
          sku: cartItem.product.sku,
          assortment: cartItem.product.assortment,
          price: this.priceActive ? cartItem.product.price : 0,
        },
        price_text: this.priceActive
          ? formatCurrency(
              cartItem.product.price ? cartItem.product.price : 0,
              this.lang,
              this.currencySymbol
            )
          : '',
        collection: {
          texts: {
            title: i18next.t(
              this.collectionsService.getCollectionData(cartItem.collectionId)
                .texts.title
            ),
          },
        },
      };
      itemsPDF.push(generatedItem);
    });
    this.dataService.generatePDF(
      itemsPDF,
      form.value.vendorName,
      this.lang,
      pdfAddress,
      translations
    );
    this.closeModal('generate-pdf');
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  async b2bNewCart() {
    try {
      this.missingSkus = [];
      const token = await this.authService.getSalesForceToken();
      if (!token) {
        this.openModal('sf-login');
        return;
      }
      const items = this.cartItems ?? [];
      const result = await this.b2bService.checkProductsAvailable(items, token);
      if (!result.success) {
        const mySkus = items.map((x) => x.product.sku);
        const availableSkus = result.skuList;
        // alle skus die ich versuche zu übertragen - die skus die es für den benutzer gibt
        const missing = mySkus.filter(
          (x) => !availableSkus.includes(x.toString())
        );

        this.missingSkus = missing;
        this.openModal('new-cart-missing-skus');
        return;
      }
      const createResult = await this.b2bService.createToCart(items, token);
      this.toastr.success(i18next.t('cart.b2b.success'));
      this.analyticsService.trackEventWithCurrency({
        event: 'digital bag',
        action: 'create servicewelt b2b shop cart',
        category: 'bpl app',
        cartValue: formatNumber(
          items.map((x) => x.quantity * x.price).reduce((acc, x) => x + acc, 0)
        ),
      });
      this.jumpToB2b();
      this.transferedSkus = createResult.skuList.map((x) => +x);
    } catch (e) {
      this.toastr.error(extractErrorMessage(e), undefined, {
        disableTimeOut: true,
      });
    }
  }

  async b2bNewCartResume() {
    try {
      const token = await this.authService.getSalesForceToken();
      if (!token) {
        this.openModal('sf-login');
        return;
      }

      this.closeModal('new-cart-missing-skus');
      const items = this.cartItems ?? [];
      const createResult = await this.b2bService.createToCart(items, token);
      this.toastr.success(i18next.t('cart.b2b.success'));
      this.analyticsService.trackEventWithCurrency({
        event: 'digital bag',
        action: 'create servicewelt b2b shop cart',
        category: 'bpl app',
        cartValue: formatNumber(
          items.map((x) => x.quantity * x.price).reduce((acc, x) => x + acc, 0)
        ),
      });
      this.jumpToB2b();
      this.transferedSkus = createResult.skuList.map((x) => +x);
    } catch (e) {
      this.toastr.error(extractErrorMessage(e), undefined, {
        disableTimeOut: true,
      });
    }
  }

  async b2bAddCart() {
    try {
      this.missingSkus = [];
      const token = await this.authService.getSalesForceToken();
      if (!token) {
        this.openModal('sf-login');
        return;
      }
      const items = this.cartItems ?? [];
      const result = await this.b2bService.checkProductsAvailable(items, token);
      if (!result.success) {
        const mySkus = items.map((x) => x.product.sku);
        const availableSkus = result.skuList;
        // alle skus die ich versuche zu übertragen - die skus die es für den benutzer gibt
        const missing = mySkus.filter(
          (x) => !availableSkus.includes(x.toString())
        );

        this.missingSkus = missing;
        this.openModal('add-cart-missing-skus');
        return;
      }
      const addedResult = await this.b2bService.addToCart(items, token);
      this.toastr.success(i18next.t('cart.b2b.success'));
      this.analyticsService.trackEventWithCurrency({
        event: 'digital bag',
        action: 'update servicewelt b2b shop cart',
        category: 'bpl app',
        cartValue: formatNumber(
          items.map((x) => x.quantity * x.price).reduce((acc, x) => x + acc, 0)
        ),
      });
      this.jumpToB2b();
      this.transferedSkus = addedResult.skuList.map((x) => +x);
    } catch (e) {
      console.error(e);
      this.toastr.error(extractErrorMessage(e), undefined, {
        disableTimeOut: true,
      });
    }
  }

  async b2bAddCartResume() {
    try {
      const token = await this.authService.getSalesForceToken();
      if (!token) {
        this.openModal('sf-login');
        return;
      }

      this.closeModal('add-cart-missing-skus');
      const items = this.cartItems ?? [];
      const addedResult = await this.b2bService.addToCart(items, token);
      this.toastr.success(i18next.t('cart.b2b.success'));
      this.analyticsService.trackEventWithCurrency({
        event: 'digital bag',
        action: 'update servicewelt b2b shop cart',
        category: 'bpl app',
        cartValue: formatNumber(
          items.map((x) => x.quantity * x.price).reduce((acc, x) => x + acc, 0)
        ),
      });
      this.jumpToB2b();
      this.transferedSkus = addedResult.skuList.map((x) => +x);
    } catch (e) {
      this.toastr.error(extractErrorMessage(e), undefined, {
        disableTimeOut: true,
      });
    }
  }

  async sfLogin() {
    if (!this.sflogniform.valid) {
      return;
    }
    try {
      await this.authService.authenticateSalesForce(
        this.sflogniform.value.username,
        this.sflogniform.value.password
      );
      this.toastr.success(i18next.t('cart.sflogin.success'));
      this.closeModal('sf-login');
    } catch (_e) {
      this.toastr.error(i18next.t('cart.sflogin.error'));
    }
  }

  b2bReset() {
    //this.missingSkus = [];
    this.closeModal('new-cart-missing-skus');
    this.closeModal('add-cart-missing-skus');
    this.closeModal('sf-login');
  }

  missingSkuText() {
    const items = [];
    for (let i = 0; i < this.missingSkus.length; i++) {
      const sku = this.missingSkus[i];
      const item = this.cartItems?.find((x) => x.product.sku === sku);
      if (item) {
        items.push(item);
      }
    }

    return items.map(
      (x) => `${i18next.t(x.product.texts.caption)} (${x.product.sku})`
    );
  }

  communityData: {
    osit: string;
    sid: string;
    username: string;
  } | null = null;

  jumpToB2b() {
    console.log('communityData', this.communityData);
    if (!this.communityData) {
      return null;
    }
    console.log(
      `${environment.servicePortal}/login?userName=${encodeURIComponent(
        this.communityData.username
      )}&authToken=${encodeURIComponent(
        this.communityData.osit
      )}&sid=${encodeURIComponent(
        this.communityData.sid
      )}&startURL=${encodeURIComponent('/account/carts')}`
    );
    if (this.nis.native.mode > BridgeMode.Mock) {
      this.nis.basics.openExternalBrowser$(
        `${environment.servicePortal}/login?userName=${encodeURIComponent(
          this.communityData.username
        )}&authToken=${encodeURIComponent(
          this.communityData.osit
        )}&sid=${encodeURIComponent(
          this.communityData.sid
        )}&startURL=${encodeURIComponent('/account/carts')}`
      );
    } else {
      const a = document.createElement('a');
      a.href = `${
        environment.servicePortal
      }/login?userName=${encodeURIComponent(
        this.communityData.username
      )}&authToken=${encodeURIComponent(
        this.communityData.osit
      )}&sid=${encodeURIComponent(
        this.communityData.sid
      )}&startURL=${encodeURIComponent('/account/carts')}`;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    }
    // ? why
    return null;
  }
}
